/*Bearer ${token}`
 * ChargesTab — Full billing dashboard: jobs, tasks, fees & charges
 *
 * Shows every job for the client with:
 *   - Service, title, period, due date, status
 *   - Task progress (expandable task list)
 *   - Fee from the tenant fee schedule
 *   - Linked charge status (draft / sent / paid) if one has been generated
 *   - Actions: generate charge, mark invoiced, mark paid
 *
 * Sections:
 *   1. Summary cards (outstanding / draft / paid)
 *   2. Fee Schedule Editor (collapsible)
 *   3. All Jobs (Active → Completed) with per-job task + billing detail
 *   4. Standalone charges list (charges without a matched job)
 */

import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import ReceiptLongIcon  from "@mui/icons-material/ReceiptLong";
import SettingsIcon     from "@mui/icons-material/Settings";
import AutoAwesomeIcon  from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon  from "@mui/icons-material/CheckCircle";
import SendIcon         from "@mui/icons-material/Send";
import ExpandMoreIcon   from "@mui/icons-material/ExpandMore";
import ExpandLessIcon   from "@mui/icons-material/ExpandLess";
import VuiBox          from "components/VuiBox";
import VuiTypography   from "components/VuiTypography";
import VuiButton       from "components/VuiButton";
import {
  getFeeSchedule,
  updateFeeSchedule,
  listCharges,
  generateCharges,
  updateChargeStatus,
  listClientJobs,
  listJobTasks,
  updateClientJob,
} from "services/tenantUserAdminService";
import { TC, FS, FW } from "../../tokens/text";

// ── Constants ─────────────────────────────────────────────────────────────────

const CARD = {
  background: "#ffffff",
  border: "1px solid #d8e6f7",
  borderRadius: "12px",
};

const INNER_CARD = {
  background: "#f4f8ff",
  border: "1px solid #d8e6f7",
  borderRadius: "8px",
};

const CHARGE_STATUS = {
  draft: { label: "Draft",  color: TC.link,    bg: "rgba(144,202,249,0.12)", border: "rgba(144,202,249,0.4)" },
  sent:  { label: "Sent",   color: TC.warning, bg: "rgba(255,152,0,0.12)",   border: "rgba(255,152,0,0.4)"   },
  paid:  { label: "Paid",   color: TC.success, bg: "rgba(76,175,80,0.12)",   border: "rgba(76,175,80,0.4)"   },
};

const JOB_STATUS_COLOR = {
  pending:   "#90caf9",
  in_progress:"#0075ff",
  review:    "#ff9800",
  approved:  "#00bcd4",
  filed:     "#4caf50",
  invoiced:  "#4caf50",
  cancelled: "#9e9e9e",
  overdue:   "#f44336",
};

const SERVICE_LABELS = {
  vat:                 "VAT",
  payroll:             "PAYE",
  accounts:            "Accounts Prep",
  corporation_tax:     "CT600",
  self_assessment:     "SA",
  company_secretarial: "CS01",
  mtd_quarterly:       "MTD",
  ad01_address_change: "AD01",
};

const SERVICE_COLORS = {
  vat:                 "#7c4dff",
  payroll:             "#0075ff",
  accounts:            "#00bcd4",
  corporation_tax:     "#ff6d00",
  self_assessment:     "#e91e63",
  company_secretarial: "#4caf50",
  mtd_quarterly:       "#ff9800",
  ad01_address_change: "#0057d8",
};

const SERVICE_ORDER = [
  "vat", "payroll", "accounts", "corporation_tax",
  "self_assessment", "company_secretarial", "mtd_quarterly", "ad01_address_change",
];

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtGBP(amount) {
  if (amount === null || amount === undefined) return "—";
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(amount);
}

// ── Summary Card ──────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub, color, icon }) {
  return (
    <VuiBox p={2} sx={{ ...CARD, textAlign: "center" }}>
      <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 0.5 }}>
        {icon} {label}
      </VuiTypography>
      <VuiTypography variant="h6" sx={{ color, fontSize: "1.15rem", fontWeight: FW.bold }}>{value}</VuiTypography>
      {sub && (
        <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.6rem", display: "block", mt: 0.3 }}>{sub}</VuiTypography>
      )}
    </VuiBox>
  );
}

// ── Fee Schedule Editor ───────────────────────────────────────────────────────

function FeeScheduleEditor({ tenantId, schedule, onSaved }) {
  const [open,   setOpen]   = useState(false);
  const [fees,   setFees]   = useState({});
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");
  const [saved,  setSaved]  = useState(false);

  useEffect(() => {
    if (schedule?.fees) setFees({ ...schedule.fees });
  }, [schedule]);

  const handleSave = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      const result = await updateFeeSchedule(tenantId, fees);
      onSaved(result);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err?.message || "Failed to save fee schedule.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <VuiBox mb={2} sx={{ ...CARD, overflow: "hidden" }}>
      <VuiBox px={2} py={1.5} display="flex" alignItems="center" justifyContent="space-between"
        onClick={() => setOpen(o => !o)}
        sx={{ cursor: "pointer", "&:hover": { background: "#f9fbff" } }}>
        <VuiBox display="flex" alignItems="center" gap={1}>
          <SettingsIcon sx={{ color: TC.link, fontSize: "1rem" }} />
          <VuiTypography variant="caption" sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.sm }}>
            Fee Schedule
          </VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs }}>
            — global rates applied when generating charges
          </VuiTypography>
        </VuiBox>
        <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
          {open ? "▲" : "▼"}
        </VuiTypography>
      </VuiBox>

      <Collapse in={open}>
        <Divider sx={{ borderColor: "#d8e6f7" }} />
        <VuiBox p={2}>
          <Grid container spacing={1.5} mb={2}>
            {SERVICE_ORDER.map((svcId) => (
              <Grid item xs={6} sm={4} md={3} key={svcId}>
                <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block", mb: 0.4 }}>
                  {schedule?.serviceLabels?.[svcId] || SERVICE_LABELS[svcId] || svcId}
                </VuiTypography>
                <VuiBox display="flex" alignItems="center" gap={0.5}>
                  <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>£</VuiTypography>
                  <input
                    type="number" min="0" step="0.01"
                    value={fees[svcId] ?? 0}
                    onChange={(e) => setFees(prev => ({ ...prev, [svcId]: parseFloat(e.target.value) || 0 }))}
                    style={{
                      width: "100%", background: "#f4f8ff",
                      border: "1px solid #d8e6f7", borderRadius: 6,
                      padding: "5px 8px", color: TC.primary, fontSize: FS.md, outline: "none",
                    }}
                  />
                </VuiBox>
              </Grid>
            ))}
          </Grid>
          {error && (
            <VuiTypography variant="caption" sx={{ color: TC.error, fontSize: FS.sm, display: "block", mb: 1 }}>{error}</VuiTypography>
          )}
          <VuiBox display="flex" alignItems="center" gap={1.5}>
            <VuiButton size="small" color="info" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={12} sx={{ color: "inherit", mr: 0.5 }} /> : null}
              {saving ? "Saving…" : "Save Fee Schedule"}
            </VuiButton>
            {saved && (
              <VuiTypography variant="caption" sx={{ color: TC.success, fontSize: FS.sm }}>✓ Saved</VuiTypography>
            )}
            {schedule?.updatedAt && (
              <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: FS.xs }}>
                Last updated {fmt(schedule.updatedAt)}{schedule.updatedBy ? ` by ${schedule.updatedBy}` : ""}
              </VuiTypography>
            )}
          </VuiBox>
        </VuiBox>
      </Collapse>
    </VuiBox>
  );
}

// ── Job Billing Card ──────────────────────────────────────────────────────────

function JobBillingCard({ job, tenantId, feeSchedule, linkedCharge, onChargeStatusChange, updatingCharge }) {
  const [expanded,     setExpanded]     = useState(false);
  const [tasks,        setTasks]        = useState(null);
  const [tasksLoading, setTasksLoading] = useState(false);

  const svcColor = SERVICE_COLORS[job.serviceId] || "#0075ff";
  const svcLabel = SERVICE_LABELS[job.serviceId] || job.serviceId;
  const fee      = feeSchedule?.fees?.[job.serviceId];
  const isOverdue = job.dueDate && new Date(job.dueDate) < new Date()
    && job.status !== "filed" && job.status !== "invoiced" && job.status !== "cancelled";

  const pct = job.taskCount
    ? Math.round((job.completedCount / job.taskCount) * 100)
    : 0;

  const statusColor = JOB_STATUS_COLOR[job.status] || "#90caf9";
  const chargeStatus = linkedCharge ? (CHARGE_STATUS[linkedCharge.status] || CHARGE_STATUS.draft) : null;

  const handleToggle = async () => {
    if (!expanded && tasks === null) {
      setTasksLoading(true);
      try {
        const res = await listJobTasks(tenantId, job.id);
        setTasks(res.tasks || []);
      } finally {
        setTasksLoading(false);
      }
    }
    setExpanded(v => !v);
  };

  return (
    <VuiBox sx={{ ...CARD, overflow: "hidden", borderLeft: `3px solid ${svcColor}` }}>
      {/* ── Job header row ── */}
      <VuiBox
        px={2} py={1.5}
        display="flex" alignItems="center" justifyContent="space-between"
        gap={1.5} flexWrap="wrap"
        onClick={handleToggle}
        sx={{ cursor: "pointer", "&:hover": { background: "#fafcff" } }}
      >
        {/* Left: service + title + period */}
        <VuiBox display="flex" alignItems="center" gap={1.2} flexWrap="wrap" flex={1} minWidth={0}>
          <Chip
            label={svcLabel}
            size="small"
            sx={{
              height: 18, fontSize: "0.6rem", fontWeight: FW.bold,
              bgcolor: `${svcColor}18`, color: svcColor,
              border: `1px solid ${svcColor}44`, flexShrink: 0,
            }}
          />
          <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.md, fontWeight: FW.bold }}>
            {job.title}
          </VuiTypography>
          {job.periodLabel && job.periodLabel !== job.title && (
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs }}>
              {job.periodLabel}
            </VuiTypography>
          )}
          {/* Job status */}
          <Chip
            label={(job.status || "pending").replace("_", " ")}
            size="small"
            sx={{
              height: 17, fontSize: "0.58rem", fontWeight: FW.bold,
              bgcolor: `${statusColor}18`, color: statusColor,
              border: `1px solid ${statusColor}44`, flexShrink: 0,
            }}
          />
          {isOverdue && (
            <Chip label="OVERDUE" size="small"
              sx={{ height: 17, fontSize: "0.58rem", fontWeight: FW.bold,
                bgcolor: "rgba(244,67,54,0.12)", color: TC.error, border: "1px solid #f44336" }} />
          )}
        </VuiBox>

        {/* Right: due date + fee + charge + expand */}
        <VuiBox display="flex" alignItems="center" gap={2} flexShrink={0} flexWrap="wrap">
          <VuiBox textAlign="right">
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block" }}>
              Due
            </VuiTypography>
            <VuiTypography variant="caption" sx={{ color: isOverdue ? TC.error : TC.primary, fontSize: FS.sm, fontWeight: FW.semiBold }}>
              {job.dueDate ? fmt(job.dueDate) : "—"}
            </VuiTypography>
          </VuiBox>

          <VuiBox textAlign="right">
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block" }}>
              Fee
            </VuiTypography>
            <VuiTypography variant="caption" sx={{ color: fee != null ? TC.primary : TC.tertiary, fontSize: FS.sm, fontWeight: FW.semiBold }}>
              {fee != null ? fmtGBP(fee) : "Not set"}
            </VuiTypography>
          </VuiBox>

          {linkedCharge ? (
            <VuiBox textAlign="right">
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block" }}>
                Charge
              </VuiTypography>
              <VuiBox display="flex" alignItems="center" gap={0.6}>
                <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.sm, fontWeight: FW.bold }}>
                  {fmtGBP(linkedCharge.amount)}
                </VuiTypography>
                <Chip
                  label={chargeStatus.label}
                  size="small"
                  sx={{ height: 16, fontSize: "0.55rem", fontWeight: FW.bold,
                    bgcolor: chargeStatus.bg, color: chargeStatus.color, border: `1px solid ${chargeStatus.border}` }}
                />
              </VuiBox>
            </VuiBox>
          ) : (
            <VuiBox textAlign="right">
              <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: FS.xs }}>
                No charge raised
              </VuiTypography>
            </VuiBox>
          )}

          <VuiBox display="flex" alignItems="center" gap={0.5}>
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs }}>
              {pct}%
            </VuiTypography>
            {expanded
              ? <ExpandLessIcon sx={{ color: TC.secondary, fontSize: "1rem" }} />
              : <ExpandMoreIcon sx={{ color: TC.secondary, fontSize: "1rem" }} />
            }
          </VuiBox>
        </VuiBox>
      </VuiBox>

      {/* ── Task progress bar ── */}
      {job.taskCount > 0 && (
        <VuiBox sx={{ height: 3, background: "#eef2f7" }}>
          <VuiBox sx={{ height: 3, width: `${pct}%`, background: svcColor, transition: "width 0.4s" }} />
        </VuiBox>
      )}

      {/* ── Expanded: tasks + details + charge actions ── */}
      <Collapse in={expanded}>
        <Divider sx={{ borderColor: "#e8f0fb" }} />
        <VuiBox p={2} display="flex" flexDirection="column" gap={1.5}>

          {/* Tasks section */}
          <VuiBox>
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: FW.bold, display: "block", mb: 1 }}>
              Tasks {job.taskCount > 0 ? `(${job.completedCount || 0}/${job.taskCount} done)` : ""}
            </VuiTypography>

            {tasksLoading ? (
              <VuiBox display="flex" justifyContent="center" py={1.5}>
                <CircularProgress size={20} sx={{ color: TC.link }} />
              </VuiBox>
            ) : !tasks || tasks.length === 0 ? (
              <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: FS.sm }}>
                No tasks for this job.
              </VuiTypography>
            ) : (
              <VuiBox display="flex" flexDirection="column" gap={0.6}>
                {tasks.map((task) => (
                  <VuiBox key={task.id} display="flex" alignItems="center" gap={1.2}
                    px={1.2} py={0.8} sx={{ ...INNER_CARD }}>
                    <VuiBox sx={{
                      width: 14, height: 14, borderRadius: "3px", flexShrink: 0,
                      border: task.status === "done" ? "none" : `1.5px solid ${TC.secondary}66`,
                      background: task.status === "done" ? "#4caf50" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {task.status === "done" && (
                        <span style={{ color: "#fff", fontSize: "0.55rem", lineHeight: 1 }}>✓</span>
                      )}
                    </VuiBox>
                    <VuiTypography variant="caption" sx={{
                      color: task.status === "done" ? TC.tertiary : TC.primary,
                      textDecoration: task.status === "done" ? "line-through" : "none",
                      fontSize: FS.sm, flex: 1,
                    }}>
                      {task.title}
                    </VuiTypography>
                    {task.assignedName && (
                      <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: FS.xs }}>
                        {task.assignedName}
                      </VuiTypography>
                    )}
                    {task.updatedAt && task.status === "done" && (
                      <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.6rem" }}>
                        {fmt(task.updatedAt)}
                      </VuiTypography>
                    )}
                  </VuiBox>
                ))}
              </VuiBox>
            )}
          </VuiBox>

          {/* Job details row */}
          <VuiBox display="flex" gap={3} flexWrap="wrap" px={0.5}>
            <VuiBox>
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block" }}>Service</VuiTypography>
              <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.sm, fontWeight: FW.semiBold }}>
                {SERVICE_LABELS[job.serviceId] || job.serviceId}
              </VuiTypography>
            </VuiBox>
            {job.periodLabel && (
              <VuiBox>
                <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block" }}>Period</VuiTypography>
                <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.sm, fontWeight: FW.semiBold }}>{job.periodLabel}</VuiTypography>
              </VuiBox>
            )}
            <VuiBox>
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block" }}>Created</VuiTypography>
              <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.sm, fontWeight: FW.semiBold }}>{fmt(job.createdAt)}</VuiTypography>
            </VuiBox>
            {job.notes && (
              <VuiBox flex={1} minWidth={120}>
                <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block" }}>Notes</VuiTypography>
                <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.sm }}>{job.notes}</VuiTypography>
              </VuiBox>
            )}
          </VuiBox>

          {/* Charge action buttons */}
          {linkedCharge && (
            <VuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap"
              px={1.5} py={1} sx={{ background: `${chargeStatus.bg}`, border: `1px solid ${chargeStatus.border}`, borderRadius: "8px" }}>
              <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.sm, fontWeight: FW.semiBold, flex: 1 }}>
                Charge: {fmtGBP(linkedCharge.amount)} · {linkedCharge.description}
              </VuiTypography>
              {updatingCharge === linkedCharge.id
                ? <CircularProgress size={14} sx={{ color: TC.link }} />
                : (
                  <>
                    {linkedCharge.status === "draft" && (
                      <Tooltip title="Mark as Sent to client">
                        <span>
                          <VuiButton size="small" variant="outlined"
                            onClick={() => onChargeStatusChange(linkedCharge.id, "sent")}
                            startIcon={<SendIcon sx={{ fontSize: "11px !important" }} />}
                            sx={{ fontSize: "0.62rem", py: 0.3, px: 0.9, minWidth: "unset",
                              borderColor: "rgba(255,152,0,0.4)", color: TC.warning,
                              "&:hover": { borderColor: "#ff9800", background: "rgba(255,152,0,0.08)" } }}>
                            Send
                          </VuiButton>
                        </span>
                      </Tooltip>
                    )}
                    {(linkedCharge.status === "draft" || linkedCharge.status === "sent") && (
                      <Tooltip title="Mark as Paid">
                        <span>
                          <VuiButton size="small" variant="outlined"
                            onClick={() => onChargeStatusChange(linkedCharge.id, "paid")}
                            startIcon={<CheckCircleIcon sx={{ fontSize: "11px !important" }} />}
                            sx={{ fontSize: "0.62rem", py: 0.3, px: 0.9, minWidth: "unset",
                              borderColor: "rgba(76,175,80,0.4)", color: TC.success,
                              "&:hover": { borderColor: "#4caf50", background: "rgba(76,175,80,0.08)" } }}>
                            Paid
                          </VuiButton>
                        </span>
                      </Tooltip>
                    )}
                    {linkedCharge.sentAt && (
                      <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.6rem" }}>
                        Sent {fmt(linkedCharge.sentAt)}
                      </VuiTypography>
                    )}
                    {linkedCharge.paidAt && (
                      <VuiTypography variant="caption" sx={{ color: TC.success, fontSize: "0.6rem" }}>
                        ✓ Paid {fmt(linkedCharge.paidAt)}
                      </VuiTypography>
                    )}
                  </>
                )
              }
            </VuiBox>
          )}
        </VuiBox>
      </Collapse>
    </VuiBox>
  );
}

// ── Standalone Charge Row (charges without a matched job) ─────────────────────

function StandaloneChargeRow({ charge, onStatusChange, updating }) {
  const cfg = CHARGE_STATUS[charge.status] || CHARGE_STATUS.draft;

  return (
    <VuiBox display="flex" alignItems="center" justifyContent="space-between"
      px={1.5} py={1.2} sx={{ ...INNER_CARD }}>
      <VuiBox flex={1} minWidth={0}>
        <VuiBox display="flex" alignItems="center" gap={0.8} flexWrap="wrap">
          <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.sm, fontWeight: FW.semiBold }}>
            {charge.description}
          </VuiTypography>
          <Chip label={charge.serviceName || charge.serviceId} size="small"
            sx={{ height: 17, fontSize: "0.58rem", bgcolor: "rgba(0,117,255,0.12)", color: TC.link, border: "1px solid rgba(0,117,255,0.3)" }} />
          {charge.periodLabel && (
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs }}>{charge.periodLabel}</VuiTypography>
          )}
        </VuiBox>
        <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.62rem", display: "block", mt: 0.2 }}>
          Created {fmt(charge.createdAt)}
          {charge.sentAt && ` · Sent ${fmt(charge.sentAt)}`}
          {charge.paidAt && ` · Paid ${fmt(charge.paidAt)}`}
        </VuiTypography>
      </VuiBox>

      <VuiBox display="flex" alignItems="center" gap={1} ml={1.5} flexShrink={0}>
        <VuiTypography variant="button" sx={{ color: TC.primary, fontSize: FS.md, fontWeight: FW.bold, minWidth: 60, textAlign: "right" }}>
          {fmtGBP(charge.amount)}
        </VuiTypography>
        <Chip label={cfg.label} size="small"
          sx={{ height: 18, fontSize: "0.6rem", fontWeight: FW.bold,
            bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }} />
        {updating === charge.id
          ? <CircularProgress size={14} sx={{ color: TC.link }} />
          : (
            <>
              {charge.status === "draft" && (
                <Tooltip title="Mark as Sent">
                  <span>
                    <VuiButton size="small" variant="outlined"
                      onClick={() => onStatusChange(charge.id, "sent")}
                      startIcon={<SendIcon sx={{ fontSize: "11px !important" }} />}
                      sx={{ fontSize: "0.62rem", py: 0.25, px: 0.8, minWidth: "unset",
                        borderColor: "rgba(255,152,0,0.4)", color: TC.warning,
                        "&:hover": { borderColor: "#ff9800", background: "rgba(255,152,0,0.08)" } }}>
                      Send
                    </VuiButton>
                  </span>
                </Tooltip>
              )}
              {(charge.status === "draft" || charge.status === "sent") && (
                <Tooltip title="Mark as Paid">
                  <span>
                    <VuiButton size="small" variant="outlined"
                      onClick={() => onStatusChange(charge.id, "paid")}
                      startIcon={<CheckCircleIcon sx={{ fontSize: "11px !important" }} />}
                      sx={{ fontSize: "0.62rem", py: 0.25, px: 0.8, minWidth: "unset",
                        borderColor: "rgba(76,175,80,0.4)", color: TC.success,
                        "&:hover": { borderColor: "#4caf50", background: "rgba(76,175,80,0.08)" } }}>
                      Paid
                    </VuiButton>
                  </span>
                </Tooltip>
              )}
            </>
          )
        }
      </VuiBox>
    </VuiBox>
  );
}

// ── Section Heading ───────────────────────────────────────────────────────────

function SectionHeading({ label, count, right }) {
  return (
    <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={1.2}>
      <VuiBox display="flex" alignItems="center" gap={1}>
        <VuiTypography variant="caption" sx={{
          color: TC.secondary, fontSize: FS.xs, textTransform: "uppercase",
          letterSpacing: "0.09em", fontWeight: FW.bold,
        }}>
          {label}
        </VuiTypography>
        {count != null && (
          <Chip label={count} size="small"
            sx={{ height: 16, fontSize: "0.55rem", bgcolor: "#eef2f7", color: TC.secondary, border: "1px solid #d8e6f7" }} />
        )}
      </VuiBox>
      {right}
    </VuiBox>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function ChargesTab({ tenantId, clientId }) {
  const [charges,    setCharges]    = useState([]);
  const [jobs,       setJobs]       = useState([]);
  const [schedule,   setSchedule]   = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [loadError,  setLoadError]  = useState("");
  const [generating, setGenerating] = useState(false);
  const [genResult,  setGenResult]  = useState(null);
  const [genError,   setGenError]   = useState("");
  const [updating,   setUpdating]   = useState(null);

  const load = useCallback(async () => {
    if (!tenantId || !clientId) return;
    setLoading(true); setLoadError("");
    try {
      const [chargesRes, scheduleRes, jobsRes] = await Promise.all([
        listCharges(tenantId, clientId),
        getFeeSchedule(tenantId),
        listClientJobs(tenantId, clientId),
      ]);
      setCharges(chargesRes.charges || []);
      setSchedule(scheduleRes);
      setJobs(jobsRes.jobs || []);
    } catch (err) {
      setLoadError(err?.message || "Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [tenantId, clientId]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true); setGenResult(null); setGenError("");
    try {
      const result = await generateCharges(tenantId, clientId);
      setGenResult(result);
      if (result.createdCount > 0) await load();
    } catch (err) {
      setGenError(err?.message || "Failed to generate charges.");
    } finally {
      setGenerating(false);
    }
  };

  const handleChargeStatusChange = async (chargeId, newStatus) => {
    setUpdating(chargeId);
    try {
      const updated = await updateChargeStatus(tenantId, clientId, chargeId, newStatus);
      setCharges(prev => prev.map(c => c.id === chargeId ? { ...c, ...updated } : c));
    } catch (err) {
      setLoadError(err?.message || "Status update failed.");
    } finally {
      setUpdating(null);
    }
  };

  // ── Derived data ─────────────────────────────────────────────────────────────

  // Map charges by jobId for quick lookup
  const chargeByJobId = charges.reduce((m, c) => {
    if (c.jobId) m[c.jobId] = c;
    return m;
  }, {});

  // Charges with no matching job
  const jobIds = new Set(jobs.map(j => j.id));
  const standaloneCharges = charges.filter(c => !c.jobId || !jobIds.has(c.jobId));

  // Sort jobs: active first, then completed; within each group newest first
  const activeJobs    = jobs.filter(j => j.status !== "filed" && j.status !== "invoiced" && j.status !== "cancelled");
  const completedJobs = jobs.filter(j => j.status === "filed" || j.status === "invoiced");
  const cancelledJobs = jobs.filter(j => j.status === "cancelled");

  const totalPaid        = charges.filter(c => c.status === "paid").reduce((s, c) => s + (c.amount || 0), 0);
  const totalOutstanding = charges.filter(c => c.status !== "paid").reduce((s, c) => s + (c.amount || 0), 0);
  const totalDraft       = charges.filter(c => c.status === "draft").reduce((s, c) => s + (c.amount || 0), 0);
  const unbilledJobs     = activeJobs.filter(j => !chargeByJobId[j.id]).length;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <VuiBox>
      {/* Header */}
      <VuiBox mb={2.5} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
        <VuiBox display="flex" alignItems="center" gap={1}>
          <ReceiptLongIcon sx={{ color: TC.link, fontSize: "1.1rem" }} />
          <VuiTypography variant="button" sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.md }}>
            Charges &amp; Billing
          </VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs }}>
            — jobs, tasks, fees &amp; payments
          </VuiTypography>
        </VuiBox>
        <VuiButton size="small" color="info"
          onClick={handleGenerate} disabled={generating}
          startIcon={generating
            ? <CircularProgress size={12} sx={{ color: "inherit" }} />
            : <AutoAwesomeIcon sx={{ fontSize: "13px !important" }} />}>
          {generating ? "Generating…" : "Generate from Invoiced Jobs"}
        </VuiButton>
      </VuiBox>

      {/* Generate result */}
      {genResult && (
        <VuiBox mb={2} px={2} py={1} sx={{ borderRadius: "8px",
          background: genResult.createdCount > 0 ? "rgba(76,175,80,0.1)" : "rgba(144,202,249,0.08)",
          border: `1px solid ${genResult.createdCount > 0 ? "rgba(76,175,80,0.4)" : "#d8e6f7"}` }}>
          <VuiTypography variant="caption" sx={{ color: genResult.createdCount > 0 ? TC.success : TC.secondary, fontSize: FS.sm }}>
            {genResult.createdCount > 0
              ? `✓ Generated ${genResult.createdCount} new charge(s).`
              : "No new charges to generate."}
            {genResult.skippedExisting > 0 && ` ${genResult.skippedExisting} already existed.`}
            {genResult.skippedNoFee   > 0 && ` ${genResult.skippedNoFee} skipped (no fee configured).`}
          </VuiTypography>
        </VuiBox>
      )}
      {genError && (
        <VuiBox mb={2} px={2} py={1} sx={{ borderRadius: "8px", background: "rgba(244,67,54,0.08)", border: "1px solid rgba(244,67,54,0.3)" }}>
          <VuiTypography variant="caption" sx={{ color: TC.error, fontSize: FS.sm }}>⚠ {genError}</VuiTypography>
        </VuiBox>
      )}
      {loadError && (
        <VuiTypography variant="caption" sx={{ color: TC.error, fontSize: FS.sm, display: "block", mb: 1.5 }}>⚠ {loadError}</VuiTypography>
      )}

      {/* Summary cards */}
      <Grid container spacing={1.5} mb={2.5}>
        <Grid item xs={6} sm={3}>
          <SummaryCard label="Outstanding" value={fmtGBP(totalOutstanding)} color="#ff9800" icon="🕐" sub={`${charges.filter(c=>c.status!=="paid").length} charge(s)`} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard label="Draft" value={fmtGBP(totalDraft)} color={TC.link} icon="📄" sub={`${charges.filter(c=>c.status==="draft").length} charge(s)`} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard label="Paid" value={fmtGBP(totalPaid)} color="#4caf50" icon="✅" sub={`${charges.filter(c=>c.status==="paid").length} charge(s)`} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard label="Unbilled Jobs" value={unbilledJobs} color={unbilledJobs > 0 ? TC.warning : TC.tertiary} icon="📋" sub="active, no charge" />
        </Grid>
      </Grid>

      {/* Fee schedule editor */}
      <FeeScheduleEditor tenantId={tenantId} schedule={schedule} onSaved={setSchedule} />

      {/* Jobs section */}
      {loading ? (
        <VuiBox display="flex" justifyContent="center" py={5}>
          <CircularProgress size={28} sx={{ color: TC.link }} />
        </VuiBox>
      ) : jobs.length === 0 ? (
        <VuiBox p={3} sx={{ ...CARD, textAlign: "center" }}>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
            No jobs found. Jobs will appear here once they are created in the Jobs &amp; Tasks tab.
          </VuiTypography>
        </VuiBox>
      ) : (
        <>
          {/* Active jobs */}
          {activeJobs.length > 0 && (
            <VuiBox mb={3}>
              <SectionHeading label="Active Jobs" count={activeJobs.length} />
              <VuiBox display="flex" flexDirection="column" gap={1.2}>
                {activeJobs.map(job => (
                  <JobBillingCard
                    key={job.id}
                    job={job}
                    tenantId={tenantId}
                    feeSchedule={schedule}
                    linkedCharge={chargeByJobId[job.id] || null}
                    onChargeStatusChange={handleChargeStatusChange}
                    updatingCharge={updating}
                  />
                ))}
              </VuiBox>
            </VuiBox>
          )}

          {/* Completed jobs */}
          {completedJobs.length > 0 && (
            <VuiBox mb={3}>
              <SectionHeading label="Completed Jobs" count={completedJobs.length} />
              <VuiBox display="flex" flexDirection="column" gap={1.2}>
                {completedJobs.map(job => (
                  <JobBillingCard
                    key={job.id}
                    job={job}
                    tenantId={tenantId}
                    feeSchedule={schedule}
                    linkedCharge={chargeByJobId[job.id] || null}
                    onChargeStatusChange={handleChargeStatusChange}
                    updatingCharge={updating}
                  />
                ))}
              </VuiBox>
            </VuiBox>
          )}

          {/* Cancelled jobs */}
          {cancelledJobs.length > 0 && (
            <VuiBox mb={3}>
              <SectionHeading label="Cancelled Jobs" count={cancelledJobs.length} />
              <VuiBox display="flex" flexDirection="column" gap={1.2}>
                {cancelledJobs.map(job => (
                  <JobBillingCard
                    key={job.id}
                    job={job}
                    tenantId={tenantId}
                    feeSchedule={schedule}
                    linkedCharge={chargeByJobId[job.id] || null}
                    onChargeStatusChange={handleChargeStatusChange}
                    updatingCharge={updating}
                  />
                ))}
              </VuiBox>
            </VuiBox>
          )}

          {/* Standalone charges (no matching job) */}
          {standaloneCharges.length > 0 && (
            <VuiBox mb={2}>
              <SectionHeading label="Other Charges" count={standaloneCharges.length} />
              <VuiBox display="flex" flexDirection="column" gap={0.8}>
                {standaloneCharges.map(c => (
                  <StandaloneChargeRow key={c.id} charge={c} onStatusChange={handleChargeStatusChange} updating={updating} />
                ))}
              </VuiBox>
            </VuiBox>
          )}
        </>
      )}
    </VuiBox>
  );
}

